package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.frames.DeviceFrames
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.geom.aabb
import dev.lingvo.shared.screenshots.geom.scale
import dev.lingvo.shared.screenshots.geom.times
import dev.lingvo.shared.screenshots.geom.toParent
import dev.lingvo.shared.screenshots.geom.translate
import dev.lingvo.shared.screenshots.text.CaptionFitter
import kotlin.math.abs

/**
 * Fits and paints the same document for previews and exports, with platform dependencies injected.
 * Each render thread owns an instance so its bounded mutable fit cache needs no cross-platform synchronization.
 */
class Renderer(
    private val fitter: CaptionFitter,
    private val frames: DeviceFrames,
    private val fitCache: FitCache = FitCache(512),
) : ShotRenderer {
    override fun renderSlot(design: ScreenshotDesign, boardId: String, slotId: String, inputs: RenderInputs, surface: Surface): RenderReport {
        val board = requireNotNull(design.boards.find { it.id == boardId }) { "Unknown screenshot board" }
        val page = requireNotNull(board.pages.find { slotId in it.slotIds }) { "Unknown screenshot slot" }
        val index = page.slotIds.indexOf(slotId)
        require(surface.width > 0 && surface.height > 0 && board.canvas.width > 0 && board.canvas.height > 0) { "Canvas size must be positive" }
        val scale = scale(surface.width.toDouble() / board.canvas.width, surface.height.toDouble() / board.canvas.height)
        val offset = translate(-index.toDouble() * board.canvas.width, 0.0)
        val layers = effectiveLayers(page, board, inputs.locale, design.schemaVersion)
        val primaryId = if (design.schemaVersion >= 3) primaryCaptionLayer(page, layers, index)?.base?.id else null
        val session = RenderSession(design, board, page, inputs, surface, fitter, frames, fitCache, primaryId)
        surface.save()
        try {
            surface.concat(scale)
            surface.clip(Paths.rect(Rect(0.0, 0.0, board.canvas.width.toDouble(), board.canvas.height.toDouble())))
            surface.concat(offset)
            layers.forEach { session.draw(it, 1.0, scale * offset) }
        } finally { surface.restore() }
        return RenderReport(session.texts.toList(), session.missing.toList(), emptyList())
    }
}

internal class RenderSession(
    val design: ScreenshotDesign, val board: Board, val page: Page, val inputs: RenderInputs, val surface: Surface,
    val fitter: CaptionFitter, val frames: DeviceFrames, val cache: FitCache,
    val primaryCaptionId: String?,
) {
    val texts = mutableListOf<TextReport>()
    val missing = mutableListOf<MissingInput>()
    val strip = Rect(0.0, 0.0, board.canvas.width.toDouble() * page.slotIds.size, board.canvas.height.toDouble())
    private val pageStart = board.pages.takeWhile { it.id != page.id }.sumOf { it.slotIds.size }
    private val planSize = board.pages.sumOf { it.slotIds.size }

    fun slot(local: Int, offset: Int = 0): Int = ((pageStart + local + offset) % planSize + planSize) % planSize

    fun image(source: ImageSource, layer: Layer): ImageHandle? {
        val index = if (source is ImageSource.Capture) slot(source.slot, source.planOffset) else slot(layer.base.roleSlot)
        return inputs.images(source, index)?.also {
            require(it.width > 0 && it.height > 0) { "Image dimensions must be positive" }
        } ?: run {
            missing += when (source) {
                is ImageSource.Capture -> MissingInput.Capture(layer.base.id, index)
                is ImageSource.Asset -> MissingInput.Asset(layer.base.id, source.assetId)
                ImageSource.BrandLogo -> MissingInput.Logo(layer.base.id)
            }
            null
        }
    }

    fun draw(layer: Layer, parentAlpha: Double, parent: Affine,
        clip: Rect = Rect(0.0, 0.0, surface.width.toDouble(), surface.height.toDouble())) {
        if (layer.base.hidden || parentAlpha * layer.base.opacity == 0.0) return
        val box = layer.base.box
        val local = Rect(0.0, 0.0, box.w, box.h)
        val matrix = if (layer is BackgroundLayer) Affine.IDENTITY else box.toParent()
        val world = parent * matrix
        val alpha = parentAlpha * layer.base.opacity
        val index = slot(layer.base.roleSlot)
        val shadow = layer.base.shadow?.let { resolveShadow(it, world, design.palette, index) }
        if (!visibleInput(layer, parent, world, shadow, clip)) return
        surface.save()
        try {
            if (layer !is BackgroundLayer) surface.concat(matrix)
            surface.setAlpha(alpha)
            surface.setBlend(layer.base.blend)
            surface.setShadow(shadow)
            when (layer) {
                is BackgroundLayer -> paintBackground(layer, strip, design.palette, index, surface)
                is ShapeLayer -> paintShape(layer, local, design.palette, index, surface)
                is ImageLayer -> paintImage(layer, local, image(layer.source, layer), inputs.mode, design.palette, index, surface)
                is TextLayer -> paintText(layer, local, this)
                is DeviceLayer -> paintDevice(layer, local, image(layer.screen, layer), this, shadow)
                is GroupLayer -> {
                    val childClip = if (layer.clip) {
                        surface.clip(Paths.roundedRect(local, layer.cornerRadius))
                        intersection(clip, aabb(box, parent)) ?: return
                    } else clip
                    layer.children.forEach { draw(it, alpha, world, childClip) }
                }
            }
        } finally { surface.restore() }
    }

    private fun visibleInput(layer: Layer, parent: Affine, world: Affine, shadow: DeviceShadow?, clip: Rect): Boolean {
        // Shapes may have curve knots outside their boxes; unclipped groups may have overflowing children.
        if (layer !is TextLayer && layer !is DeviceLayer && layer !is ImageLayer) return true
        val bounds = aabb(layer.base.box, parent)
        val stroke = if (layer is ImageLayer) (layer.stroke?.width ?: 0.0) / 2 else 0.0
        val border = stroke * maxOf(abs(world.a) + abs(world.c), abs(world.b) + abs(world.d))
        val blur = (shadow?.blur ?: 0.0) * 3
        val left = bounds.x - border + minOf(0.0, (shadow?.dx ?: 0.0) - blur)
        val top = bounds.y - border + minOf(0.0, (shadow?.dy ?: 0.0) - blur)
        val right = bounds.right + border + maxOf(0.0, (shadow?.dx ?: 0.0) + blur)
        val bottom = bounds.bottom + border + maxOf(0.0, (shadow?.dy ?: 0.0) + blur)
        return Rect(left, top, right - left, bottom - top).intersects(clip)
    }

    private fun intersection(first: Rect, second: Rect): Rect? {
        val left = maxOf(first.x, second.x)
        val top = maxOf(first.y, second.y)
        val right = minOf(first.right, second.right)
        val bottom = minOf(first.bottom, second.bottom)
        return if (right <= left || bottom <= top) null else Rect(left, top, right - left, bottom - top)
    }
}
